import BossModel from "@/models/BossModel";
import constants from "@/constants";
import Product from '@/models/Product/Product'
import ProductAddress from '@/models/Product/ProductAddress/ProductAddress'
import Country from '@/models/Country/Country'
import Type from '@/models/Product/Type/Type';
import Region from "@/models/Region/Region";
import WebsiteProduct from "@/models/WebsiteProduct/WebsiteProduct";
import moment from "moment";
import orderBy from 'lodash/orderBy';
import Code from "@/models/Agent/Code/Code";
import Margin from "@/models/Margin/Margin";
import ProductTypeIcon from "@/models/Product/Type/Icon/Icon";
import City from "@/models/City/City";
import AvailableDay from "@/models/Product/AvailableDay/AvailableDay";
import AvailableDate from "@/models/Product/AvailableDate/AvailableDate";
import Flight from "@/models/Flight/Flight";

export default class ProductVersion extends BossModel {

    /**
     *
     * @type {string}
     */
    static apiUrl = 'product/version';

    /**
     *
     * @type {string}
     */
    static entity = 'productVersions';

    static fields() {
        return {
            id: this.number(null).nullable(),
            product_id: this.number(null).nullable(),
            product_type_id: this.number(null).nullable(),
            product_type_icon_id: this.number(null).nullable(),
            country_id: this.number(null).nullable(),
            city_id: this.number(null).nullable(),
            code: this.attr(null),
            full_code: this.attr(null),
            name: this.attr(null),
            url: this.attr(null),
            agent_code_id: this.number(null).nullable(),
            agent_name: this.attr(null),
            subtitle: this.attr(null),
            description: this.attr(null),
            text_voucher: this.attr(null),
            stars: this.number(null).nullable(),
            unit_cid: this.attr(null),
            margin_id: this.number(null).nullable(),
            default_duration: this.attr(null),
            is_active: this.boolean(null),
            is_published: this.boolean(null),
            is_need_passport: this.boolean(false),
            is_intern: this.boolean(null),
            is_sale_preferred: this.boolean(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            deleted_at: this.attr(null),
            product: this.belongsTo(Product, 'product_id'),
            type: this.belongsTo(Type, 'product_type_id'),
            icon: this.belongsTo(ProductTypeIcon, 'product_type_icon_id'),
            country: this.belongsTo(Country, 'country_id'),
            city: this.belongsTo(City, 'city_id'),
            agentCode: this.belongsTo(Code, 'agent_code_id'),
            margin: this.belongsTo(Margin, 'margin_id'),
            flights: this.hasMany(Flight, 'product_version_id'),
            flight: this.hasOne(Flight, 'product_version_id'),
            websiteProduct: this.hasOne(WebsiteProduct, 'product_id', 'product_id'),
            addresses: this.hasMany(ProductAddress, 'product_id', 'product_id'),
            // firstAddress: this.hasOne(ProductAddress, 'product_id', 'product_id'),
            // lastAddress: this.hasOne(ProductAddress, 'product_id', 'product_id'),
            availableDayOfWeeks: this.hasMany(AvailableDay, 'product_id', 'product_id'),
            availableDates: this.hasMany(AvailableDate, 'product_id', 'product_id'),
        }
    }

    /**
     *
     * @param data
     * @returns {Promise<AxiosResponse<T>>}
     */
    Update(data) {
        return this.api().update(data).then(newVersion => {
            let product = Product.find(newVersion.product_id);
            product.active_version_id = newVersion.id;
            product.commit();
            return newVersion;
        });
    }

    /**
     *
     * @param filters
     * @param sortBy
     * @param paginator
     * @returns {PromiseLike<any> | Promise<any> | *}
     */
    static searchByConditions(filters, sortBy, paginator) {
        return this.api()
            .where('currentVersion', true)
            .with((['country', 'type', 'product']))
            .ordered(sortBy)
            .filtered(filters)
            .paginator(paginator)
            .get().then((response) => {
                let productVersions = [];
                if (response === undefined) {
                    return null;
                }
                response.forEach((productVersion) => {
                    productVersions.push(new ProductVersion(productVersion));
                })
                return productVersions
            });

    }

    /**
     *
     * @param filters
     * @param sortBy
     * @param withs
     * @returns {PromiseLike<any> | Promise<any> | *}
     */
    static fetchProductVersionsBySortedFilters(filters, sortBy, withs = []) {
        return this.api()
            .where('currentVersion', true)
            .where('is_active', true)
            .with((['country',
                'product.cheapestCachedProductPrice',
                'flight',
                'flights.departureAirport.city',
                'flights.arrivalAirport.city',]).concat(withs))
            .ordered(sortBy)
            .filtered(filters)
            .get().then((response) => {
                let productVersions = [];
                if (response === undefined) {
                    return null;
                }
                response.forEach((productVersion) => {
                    productVersions.push(new ProductVersion(productVersion));
                })
                return productVersions
            });

    }

    /**
     *
     * @param filters
     * @param sortBy
     * @param withs
     * @returns {PromiseLike<any> | Promise<any> | *}
     */
    static fetchHotelVersionsBySortedFilters(filters, sortBy, withs = []) {
        return this.api()
            .where('currentVersion', true)
            .where('is_active', true)
            .where('product_type_id', constants.productTypes.hotel)
            .with((['country', 'product.cheapestCachedProductPrice',]).concat(withs))
            .ordered(sortBy)
            .filtered(filters)
            .get().then((response) => {
                let productVersions = [];
                if (response === undefined) {
                    return null;
                }
                response.forEach((productVersion) => {
                    productVersions.push(new ProductVersion(productVersion));
                })
                return productVersions
            });
    }

    /**
     *
     * @param id
     * @returns {*}
     */
    static fetchProductVersionDetails(id) {
        return this.api()
            .with(['product.tags', 'flight', 'product.regions', 'product.websiteRoute'])
            .find(id, false);
    }

    /**
     *
     * @param id
     * @returns {*}
     */
    static fetchProductVersionImages(id) {
        return this.api()
            .with([
                'product',
                'websiteProduct.headerImage',
                'websiteProduct.featuredImage',
                'websiteProduct.websiteProductImages.publicImage',
            ])
            .find(id, false);

    }

    /**
     *
     * @param id
     * @returns {*}
     */
    static fetchProductVersionDaytodayImages(id) {
        return this.api()
            .with([
                'product.daytodays.images.publicImage',
            ])
            .find(id, false);
    }

    /**
     *
     * @param id
     * @returns {*}
     */
    static fetchProductVersionDetailsWithCompositions(id) {
        return this.api()
            .with('product.tags')
            .with('product.regions')
            .with('product.activeDetails')
            .with('product.activeDetails.activeCompositions')
            .find(id, false);
    }

    /**
     *
     * @param departureRegionId
     * @returns {PromiseLike<[]> | Promise<[]> | *}
     */
    static fetchTransfersByDepartureRegion(departureRegionId) {
        return this.api()
            .where('product_type_id', 5)
            .where('transferDepartureRegion', departureRegionId)
            .where('is_active', true)
            .limit(0)
            .where('currentVersion', 1)
            .with('lastAddress.region')
            .get().then((data) => {
                let instances = [];
                for (let key in data) {
                    if (data[key].lastAddress && data[key].lastAddress.region_id) {
                        if (instances.find((region) => region.id === data[key].lastAddress.region_id) === undefined) {
                            instances.push(new Region(data[key].lastAddress.region));
                        }
                    }
                }
                return instances;
            });
    }

    /**
     *
     * @param arrivalRegionId
     * @returns {PromiseLike<[]> | Promise<[]> | *}
     */
    static fetchTransfersByArrivalRegion(arrivalRegionId) {
        return this.api()
            .where('product_type_id', 5)
            .where('transferArrivalRegion', arrivalRegionId)
            .where('is_active', true)
            .limit(0)
            .where('currentVersion', 1)
            .with('firstAddress.region')
            .get().then((data) => {
                let instances = [];
                for (let key in data) {
                    if (data[key].firstAddress && data[key].firstAddress.region_id) {
                        if (instances.find((region) => region.id === data[key].firstAddress.region_id) === undefined) {
                            instances.push(new Region(data[key].firstAddress.region));
                        }
                    }
                }
                return instances;
            });
    }

    /**
     *
     * @param isPublished
     * @param destination
     * @returns {Promise<AxiosResponse<T>>}
     */
    Publish(isPublished, destination = null) {
        let apiAction = 'un-publish';
        if (isPublished) {
            apiAction = 'publish';
        }

        let data = {
            'destination_website_route_id': null,
            'http_code': null,
        };

        if (destination !== null) {
            data.destination_website_route_id = destination.destination_website_route_id;
            data.http_code = destination.http_code;
        }

        return this.constructor.api().action(this.id + '/' + apiAction).update(data, false)
            .then(productVersion => {
                return Product.api().with(['activeVersion']).find(productVersion.product_id);
            });
    }

    /**
     *
     * @returns {string|null}
     */
    FullUrl() {
        if (!this.Type() || !this.Country() || this.url == null || this.url.length === 0) {
            return null;
        }

        return this.Country().url + '/' + this.Type().url + '/' + this.url;
    }

    /**
     *
     * @returns {string}
     */
    getCode() {
        let code = '0000' + this.code;
        return code.substr(code.length - 4, code.length)
    }

    /**
     *
     * @returns {string}
     */
    getFullCode() {
        let countryCode = Country.find(this.country_id) ? Country.find(this.country_id).code : '';
        let productTypeLetter = Type.find(this.product_type_id) ? Type.find(this.product_type_id).code : '';
        return productTypeLetter + '-' + countryCode + '-' + this.getCode();
    }

    /**
     *
     * @param date
     * @returns {PromiseLike<number> | Promise<number> | *}
     */
    FlightCreatesDateDifference(date) {
        return this.constructor.api()
            .with('flight.availabilities')
            .find(this.id, false).then((res) => {
                let dateChange = 1;
                if (res.flight && res.flight.availabilities) {
                    res.flight.availabilities.forEach(av => {
                        if (moment(date).isBetween(av.start, av.end) && av.is_return === 0) {
                            dateChange = av.date_change;
                        }
                    })
                }
                return dateChange
            });
    }

    /**
     *
     * @returns {boolean|boolean}
     */
    IsFlight() {
        return this.product_type_id !== undefined && Type.find(this.product_type_id).id === Type.IDS.flight;
    }

    /**
     *
     * @returns {boolean|*}
     */
    ProductTypeIcon() {
        return this.getRelated('icon');
    }

    /**
     *
     * @returns {*}
     */
    Flight() {
        this.getRelated('flight');
    }

    /**
     *
     * @returns {boolean}
     */
    IsProduct() {
        return true;
    }

    /**
     *
     * @returns {boolean|*}
     */
    Type() {
        return this.getRelated('type');
    }

    /**
     *
     * @returns {boolean|*}
     */
    Margin() {
        return this.getRelated('margin');
    }

    /**
     *
     * @returns {}
     */
    ChargeUnit() {
        return constants.chargeUnits.find((chargeUnit) => chargeUnit.id === this.unit_cid);
    }

    /**
     *
     * @returns {boolean|*}
     */
    Country() {
        return this.getRelated('country');
    }

    City() {
        return this.getRelated('city');
    }

    /**
     *
     * @returns {*}
     */
    AvailableDates() {
        return this.getRelated('availableDates');
    }

    /**
     *
     * @returns {*}
     */
    AvailableDayOfWeeks() {
        return this.getRelated('availableDayOfWeeks');
    }

    /**
     *
     * @returns {*}
     */
    WebsiteProduct() {
        return this.getRelated('websiteProduct');
    }

    /**
     *
     * @returns {Array}
     */
    Addresses() {
        return orderBy(this.getRelated('addresses'), 'sort_order');
    }

    /**
     *
     * @returns {boolean|*}
     */
    Product() {
        return this.getRelated('product');
    }

    /**
     *
     * @returns {boolean|*}
     */
    AgentCode() {
        return this.getRelated('agentCode');
    }

    /**
     *
     * @returns {null|*}
     * @constructor
     */
    firstAddress() {
        if (!this.addresses) {
            return null;
        }
        return this.addresses[0];
    }

    /**
     *
     * @returns {null|*}
     * @constructor
     */
    lastAddress() {
        if (!this.addresses) {
            return null;
        }
        return this.addresses[this.addresses.length - 1];
    }

}
